import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { getAuthToken, initCognito, logout } from './shared/auth/session';
import { isCognitoEnabled, isNativeCognitoEnabled } from './shared/auth/cognitoFeatureFlag';
import { AppContext } from './models/AppContext';
import { registerApiBearerTokenResolver, registerApiUnauthorizedHandler } from './http/axios';

import App from './App.vue';
import store from './store';
import router from './router';
import 'virtual:vite-plugin-sentry/sentry-config';
import * as Sentry from '@sentry/vue';
import { i18n } from './i18n';

import {
  message,
  Form,
  Input,
  Button,
  Tooltip,
  Row,
  Col,
  Alert,
  Radio,
  ConfigProvider,
  Table,
  Empty,
  Badge,
  Tabs,
  Select,
  DatePicker,
  Spin,
  Switch,
  Popconfirm,
  Modal,
  Upload,
  Menu,
  Tag,
  Divider,
  Progress,
  Card,
  InputNumber,
  Collapse,
} from 'ant-design-vue';
import 'ant-design-vue/dist/antd.less';
import api from './http/api';
import http from './http/http';
import moment from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import VChart from 'vue-echarts';

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App)
  .use(pinia)
  .use(store)
  .use(i18n)
  .use(Form)
  .use(Input)
  .use(Tooltip)
  .use(Row)
  .use(Col)
  .use(Alert)
  .use(Radio)
  .use(Table)
  .use(ConfigProvider)
  .use(Empty)
  .use(Badge)
  .use(Tabs)
  .use(Select)
  .use(DatePicker)
  .use(Spin)
  .use(Switch)
  .use(Popconfirm)
  .use(Modal)
  .use(Menu)
  .use(Upload)
  .use(Divider)
  .use(Tag)
  .use(Progress)
  .use(Card)
  .use(Button)
  .use(InputNumber)
  .use(Collapse)
  .component('v-chart', VChart);

if (!isNativeCognitoEnabled) app.use(router);

registerApiBearerTokenResolver(() => getAuthToken(AppContext.SolCentre));
registerApiUnauthorizedHandler(() => logout(AppContext.SolCentre));

app.config.globalProperties.$message = message;
app.config.globalProperties.$api = api;
app.config.globalProperties.$http = http;

moment.extend(isBetween);
moment.extend(utc);
app.config.globalProperties.$moment = moment;

const dist = import.meta.env.VITE_PLUGIN_SENTRY_CONFIG.dist;
const release = import.meta.env.VITE_PLUGIN_SENTRY_CONFIG.release;
const environment = import.meta.env.VITE_SENTRY_ENV;

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    Vue: app,
    dist,
    release,
    environment,
    dsn: 'https://35a0b49e5307446ea45f543fabb122ae@o400567.ingest.sentry.io/5626771',
  });
}

async function mountApp() {
  if (isCognitoEnabled) await initCognito();
  // Native guards need the current backend profile before the first navigation.
  if (isNativeCognitoEnabled) app.use(router);
  app.mount('#app');
}

void mountApp();
